import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, Gavel, TrendingUp, Calendar, Loader2, Heart, UserRound } from "lucide-react";
import { LicensePlate } from "./LicensePlate";
import { ListingStatus } from "@/types/listing";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels, auctionsLabels } from "@/i18n";

export interface Bid {
    id: number | string;
    bidder: string;
    amount: string;
    timestamp: string;
    isHighest: boolean;
    email?: string;
}

export interface AuctionStats {
    totalViews: number;
    uniqueViewers: number;
    totalBids: number;
    uniqueBidders: number;
    favoriteCount?: number;
    currentPrice: string;
    startingPrice: string; // Admin's set starting price
    requestedPrice?: string; // Customer's requested price (from vehicleCard/carTest)
    /** Auction reserve price from ListingAuction.reservePrice */
    reservationPrice?: string;
    bids: Bid[];
    endDate: string;
}

interface MyAuctionsStatsDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    stats: AuctionStats;
    vehicleName: string;
    regNumber?: string;
    isLoading?: boolean;
    isAdmin?: boolean;
    status?: ListingStatus;
    /** Hide bid count + bid history (e.g. direct purchase). */
    hideBids?: boolean;
    /** Seller display name (person or company). Shown in admin 6th card slot. */
    sellerName?: string;
    /** Seller email (admin only). */
    sellerEmail?: string;
    /** Optional subtitle under seller name (e.g. company vs private). */
    sellerSubtitle?: string;
}

// Helper function to blur name - show only stars for non-admin
const blurName = (name: string) => {
    if (!name) return "";
    // Show only stars, no first letter
    return "•".repeat(8);
};

export const MyAuctionsStatsDialog = ({
    open,
    onOpenChange,
    stats,
    vehicleName,
    regNumber,
    isLoading = false,
    isAdmin = false,
    status,
    hideBids = false,
    sellerName,
    sellerEmail,
    sellerSubtitle,
}: MyAuctionsStatsDialogProps) => {
    const { language } = useLanguage();
    const bi = carDetailsLabels(language).biddingInfo;
    const ma = auctionsLabels(language).myAuctions;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex flex-col items-start w-full">
                        <DialogTitle className="text-left w-full">
                            {ma.stats?.statsFor ?? bi.statsFor} {vehicleName}
                        </DialogTitle>
                        {regNumber && (
                            <div className="inline-block mt-2">
                                <LicensePlate lpn={regNumber} />
                            </div>
                        )}
                    </div>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Overview Cards */}
                    <div className={`grid gap-2 ${isAdmin ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"}`}>
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Eye className="h-4 w-4" />
                                    {bi.statsViews}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{stats.totalViews}</div>
                                <div className="text-xs text-muted-foreground">
                                    {stats.uniqueViewers} {ma.stats?.uniqueVisitors ?? bi.statsUniqueViewers}
                                </div>
                            </CardContent>
                        </Card>

                        {!hideBids && (
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Gavel className="h-4 w-4" />
                                    {bi.statsBids}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{stats.totalBids}</div>
                                <div className="text-xs text-muted-foreground">
                                    {stats.uniqueBidders} {bi.statsUniqueBidders}
                                </div>
                            </CardContent>
                        </Card>
                        )}

                        {isAdmin && (
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm flex items-center gap-2">
                                        <Heart className="h-4 w-4" />
                                        {bi.statsFavorites}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">{stats.favoriteCount ?? 0}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {bi.statsFavoritesHint}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <TrendingUp className="h-4 w-4" />
                                    {bi.statsCurrentPrice}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold text-green-600 whitespace-nowrap">
                                    {stats.currentPrice}
                                </div>
                                <div className="text-xs text-muted-foreground whitespace-nowrap">
                                    {bi.statsStartedAt} {stats.startingPrice}
                                </div>
                                {stats.reservationPrice && (
                                    <div className="text-xs text-muted-foreground mt-1 whitespace-nowrap">
                                        {bi.statsReservePrice}: {stats.reservationPrice}
                                    </div>
                                )}
                                {stats.requestedPrice && (
                                    <div className="text-xs text-muted-foreground mt-1 whitespace-nowrap">
                                        {isAdmin ? bi.statsSellerPrice : bi.statsCustomerPrice}: {stats.requestedPrice}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Calendar className="h-4 w-4" />
                                    {bi.statsEnds}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-lg font-bold">{stats.endDate}</div>
                                <div className="text-xs text-muted-foreground">
                                    {bi.statsAuction}
                                </div>
                            </CardContent>
                        </Card>

                        {isAdmin && (
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm flex items-center gap-2">
                                        <UserRound className="h-4 w-4" />
                                        {bi.statsSeller || "Säljare"}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-lg font-bold leading-tight break-words">
                                        {sellerName || bi.statsSellerUnknown || "Okänd"}
                                    </div>
                                    {sellerEmail ? (
                                        <div className="text-xs text-muted-foreground mt-1 break-all">
                                            {sellerEmail}
                                        </div>
                                    ) : null}
                                    {sellerSubtitle ? (
                                        <div className="text-xs text-muted-foreground mt-1">
                                            {sellerSubtitle}
                                        </div>
                                    ) : (
                                        <div className="text-xs text-muted-foreground mt-1">
                                            {bi.statsSellerHint || "Annonsägare"}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Bidding History */}
                    {!hideBids && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Gavel className="h-5 w-5" />
                                {bi.statsBidHistory}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {isLoading ? (
                                <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
                                    <Loader2 className="h-8 w-8 animate-spin" />
                                    <p>{bi.statsLoadingBidHistory}</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {stats.bids.map((bid) => (
                                    <div
                                        key={bid.id}
                                        className={`flex items-center justify-between p-3 rounded-lg border ${bid.isHighest ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-gray-800/50'}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium">
                                                        {isAdmin ? bid.bidder : blurName(bid.bidder)}
                                                    </span>
                                                    {bid.isHighest && (
                                                        <Badge variant="default" className="bg-green-600 px-2 py-0.5 text-xs md:text-sm">
                                                            <span className="md:inline hidden">{ma.card.highestBid}</span>
                                                            <span className="md:hidden">{ma.card.highestShort}</span>
                                                        </Badge>
                                                    )}
                                                </div>
                                                {isAdmin && bid.email && (
                                                    <span className="text-xs text-muted-foreground">{bid.email}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-lg">{bid.amount}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {bid.timestamp}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                    {stats.bids.length === 0 && (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <Gavel className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                            <p>{bi.statsNoBidsYet}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
